import { engineComponent } from "neo-common";
import { Machine } from "../GameComponents/Machine";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Symbols } from "../GameComponents/Symbols";
import { ReelExtended } from "./ReelExtended";
import { CustomUtils } from "../Utils/CustomUtils";
import { WinAnimator2 } from "./WinAnimator2";

/**
 * ReelExtended handles:
 *
 */
@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    private static readonly TeasingDuration: number = 150; //in frames

    private _teasingBottom: SwitchElement;
    private _teasingTop: SwitchElement;

    private _canTease2: boolean;
    private _canTease3: boolean;
    private _canTease4: boolean;

    private _teasingAnimationStep: string;

    private _spining: boolean;

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        teasingBottom?: SwitchElement,
        teasingTop?: SwitchElement,
    ): void {
        if (teasingBottom != null) {
            this._teasingBottom = teasingBottom;
        }
        if (teasingTop != null) {
            this._teasingTop = teasingTop;
        }

        super.initialize(symbols, reels);
    }

    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        if (this._spining) {
            return;
        }
        this._spining = true;

        ReelExtended.TeaseingSymbols = [];
        WinAnimator2.Force = false;

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        if (this._reels != null) {
            for (let i = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);

                        //move current symbols from WinAnimator to Reel, not array reference
                        this._reels[i].currentSymbols = [];
                        for (let j = 0; j < this._visibleSymbolCount; j++) {
                            this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                            currentSymbols[i][j] = null;
                        }

                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            WinAnimator2.Force = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;

            PlayList.stop("SND_BaseFsTeaser");
            PlayList.stop("SND_BaseBonusTeaser");
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            if (force) {
                if (this._teasingBottom != null && this._teasingBottom.getCurrentCase() != "EMPTY") {
                    this._teasingBottom.goToCase("END");
                }
                if (this._teasingTop != null && this._teasingTop.getCurrentCase() != "EMPTY") {
                    this._teasingTop.goToCase("END");
                }
            }

            this._canTease2 = force ? false : this.checkTeasing(2);
            this._canTease3 = force ? false : this.checkTeasing(3);
            this._canTease4 = force ? false : this.checkTeasing(4);

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            //call spinStop only if not that reel will not be teased or in force stop
                            if (force || (!this._canTease2 && !this._canTease3 && !this._canTease4)) {
                                this._reels[i].spinStop(force);
                            } else {
                                if (this._canTease2 && this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1) {
                                        this._reels[i].spinStop(force);
                                    }
                                } else if (this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2) {
                                        this._reels[i].spinStop(force);
                                    }
                                } else if (this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2 || i === 3) {
                                        this._reels[i].spinStop(force);
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    public spinReset(): void {
        super.spinReset();
        this._spining = false;
    }

    public onReelStopped(reelIndex: number): void {
        this.tease(reelIndex);
        super.onReelStopped(reelIndex);

        if (this._stoppedReelIndexes === this._reelCount) {
            this._spining = false;
        }
    }

    private checkTeasing(reelIndex: number): boolean {
        let count = 0;

        for (let i = 0; i < reelIndex; i++) {
            if (this._stoppedSymbolsArray[i].symbols.indexOf(10) !== -1) {
                count++;
            }
        }

        return count >= 2;
    }

    private tease(reelIndex: number): void {
        switch (reelIndex) {
            case 1:
                this._teasingAnimationStep = "";
                if (this._canTease2) {
                    this._reels[2].spinTease([CustomUtils.SymbolScatter], MachineExtended.TeasingDuration);
                    this._reels[3].spinFakeTease();
                    this._reels[4].spinFakeTease();

                    PlayList.loop("SND_BaseTeaser");

                    this._teasingAnimationStep += "_3";
                    if (this._teasingBottom != null) {
                        this._teasingBottom.goToCase("TEASING" + this._teasingAnimationStep);
                    }
                    if (this._teasingTop != null) {
                        this._teasingTop.goToCase("TEASING" + this._teasingAnimationStep);
                    }
                }

                break;
            case 2:
                if (this._canTease3) {
                    this._reels[3].spinTease([CustomUtils.SymbolScatter], MachineExtended.TeasingDuration);
                    this._reels[4].spinFakeTease();

                    if (!this._canTease2) {
                        PlayList.loop("SND_BaseTeaser");
                    }

                    this._teasingAnimationStep += "_4";
                    if (this._teasingBottom != null) {
                        this._teasingBottom.goToCase("TEASING" + this._teasingAnimationStep);
                    }
                    if (this._teasingTop != null) {
                        this._teasingTop.goToCase("TEASING" + this._teasingAnimationStep);
                    }
                }

                break;
            case 3:
                if (this._canTease4) {
                    this._reels[4].spinTease([CustomUtils.SymbolScatter], MachineExtended.TeasingDuration);

                    if (!this._canTease3) {
                        PlayList.loop("SND_BaseTeaser");
                    }

                    this._teasingAnimationStep += "_5";
                    if (this._teasingBottom != null) {
                        this._teasingBottom.goToCase("TEASING" + this._teasingAnimationStep);
                    }
                    if (this._teasingTop != null) {
                        this._teasingTop.goToCase("TEASING" + this._teasingAnimationStep);
                    }
                }

                break;
            case 4:
                if (this._canTease4) {
                    if (this._teasingBottom != null) {
                        this._teasingBottom.goToCase("END");
                    }
                    if (this._teasingTop != null) {
                        this._teasingTop.goToCase("END");
                    }

                    PlayList.stop("SND_BaseTeaser");

                    let count = 0;

                    for (let i = 0; i <= reelIndex; i++) {
                        if (this._stoppedSymbolsArray[i].symbols.indexOf(10) !== -1) {
                            count++;
                        }
                    }

                    if (count < 3) {
                        PlayList.play("SND_BaseTeaserFail");
                    }
                }

                break;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/